/**
 * 通知记录列表页
 * 根据 notification/frontend-spec.md 规范定义
 * Tab 内展示，无页面标题
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Button,
  Space,
  Select,
  Input,
  DatePicker,
  Tag,
  Modal,
  message,
  Row,
  Col,
} from 'antd';
import {
  SendOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  pageNotificationRecords,
  resendNotifications,
} from '@/services/notification/notificationService';
import type { NotificationRecordDTO, QueryNotificationRecordPageRequest } from '@/types/notification/notificationRecord';
import {
  NotificationType,
  NotificationStatus,
  NotificationChannel,
  NotificationPriority,
  NOTIFICATION_TYPE_OPTIONS,
  NOTIFICATION_STATUS_OPTIONS,
  NOTIFICATION_CHANNEL_OPTIONS,
} from '@/constants/notification';
import NotificationRecordDetail from './components/NotificationRecordDetail';
import SendNotificationForm from './components/SendNotificationForm';

const { RangePicker } = DatePicker;

const NotificationRecordList: React.FC = () => {
  const { t } = useTranslation();

  // 列表数据
  const [dataSource, setDataSource] = useState<NotificationRecordDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 搜索条件
  const [searchParams, setSearchParams] = useState<{
    notificationId?: string;
    notificationType?: NotificationType;
    channel?: NotificationChannel;
    status?: NotificationStatus;
    targetId?: string;
    relatedOrderNo?: string;
    createTimeStart?: number;
    createTimeEnd?: number;
  }>({});

  // 选中行
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Drawer 状态
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState<NotificationRecordDTO | null>(null);
  const [sendFormVisible, setSendFormVisible] = useState(false);

  // 加载数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const params: QueryNotificationRecordPageRequest = {
        currentPage,
        pageSize,
        ...searchParams,
      };
      const response = await pageNotificationRecords(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  // 搜索
  const handleSearch = () => {
    setCurrentPage(1);
    fetchData();
  };

  // 重置
  const handleReset = () => {
    setSearchParams({});
    setCurrentPage(1);
    fetchData();
  };

  // 搜索条件变化
  const handleSearchChange = (field: string, value: any) => {
    setSearchParams(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 时间范围变化
  const handleTimeRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setSearchParams(prev => ({
        ...prev,
        createTimeStart: dates[0].startOf('day').valueOf(),
        createTimeEnd: dates[1].endOf('day').valueOf(),
      }));
    } else {
      setSearchParams(prev => ({
        ...prev,
        createTimeStart: undefined,
        createTimeEnd: undefined,
      }));
    }
  };

  // 批量重发
  const handleBatchResend = async () => {
    if (selectedRowKeys.length === 0) return;

    const failedRecords = dataSource.filter(
      (record) => selectedRowKeys.includes(record.id) && record.status === NotificationStatus.FAILED
    );

    if (failedRecords.length === 0) {
      message.warning(t('notification.record.list.message.noFailedRecord'));
      return;
    }

    Modal.confirm({
      title: t('notification.record.list.actions.batchResend'),
      content: t('notification.record.list.confirm.batchResend', { n: failedRecords.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await resendNotifications({
            notificationIds: failedRecords.map(r => r.notificationId),
          });
          if (response.success) {
            message.success(t('notification.record.list.message.batchResendSuccess'));
            setSelectedRowKeys([]);
            fetchData();
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // 查看详情
  const handleViewDetail = (record: NotificationRecordDTO) => {
    setDetailRecord(record);
    setDetailVisible(true);
  };

  // 重发单条
  const handleResend = async (record: NotificationRecordDTO) => {
    Modal.confirm({
      title: t('notification.record.list.actions.resend'),
      content: t('notification.record.list.confirm.resend'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          const response = await resendNotifications({
            notificationIds: [record.notificationId],
          });
          if (response.success) {
            message.success(t('notification.record.list.message.resendSuccess'));
            fetchData();
          } else {
            message.error(response.errorMessage || t('common.message.operationFailed'));
          }
        } catch (error) {
          message.error(t('common.message.operationFailed'));
        }
      },
    });
  };

  // 表格列定义
  const columns: ColumnsType<NotificationRecordDTO> = [
    {
      title: t('notification.record.table.notificationId'),
      dataIndex: 'notificationId',
      key: 'notificationId',
      width: 180,
      render: (text: string) => (
        <a onClick={() => {
          const record = dataSource.find(r => r.notificationId === text);
          if (record) handleViewDetail(record);
        }}>{text}</a>
      ),
    },
    {
      title: t('notification.record.table.notificationType'),
      dataIndex: 'notificationType',
      key: 'notificationType',
      width: 120,
      render: (type: NotificationType) => {
        const option = NOTIFICATION_TYPE_OPTIONS.find(opt => opt.value === type);
        return <Tag color={option?.color}>{t(option?.label || '')}</Tag>;
      },
    },
    {
      title: t('notification.record.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (channel: NotificationChannel) => {
        const option = NOTIFICATION_CHANNEL_OPTIONS.find(opt => opt.value === channel);
        return t(option?.label || '');
      },
    },
    {
      title: t('notification.record.table.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: NotificationPriority) => {
        const colors: Record<string, string> = {
          [NotificationPriority.HIGH]: 'red',
          [NotificationPriority.NORMAL]: 'blue',
          [NotificationPriority.LOW]: 'default',
        };
        return <Tag color={colors[priority]}>{t(`notification.enums.priority.${priority.toLowerCase()}`)}</Tag>;
      },
    },
    {
      title: t('notification.record.table.title'),
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: t('notification.record.table.targetName'),
      dataIndex: 'targetName',
      key: 'targetName',
      width: 120,
    },
    {
      title: t('notification.record.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: NotificationStatus) => {
        const option = NOTIFICATION_STATUS_OPTIONS.find(opt => opt.value === status);
        return <Tag color={option?.color}>{t(option?.label || '')}</Tag>;
      },
    },
    {
      title: t('notification.record.table.relatedOrderNo'),
      dataIndex: 'relatedOrderNo',
      key: 'relatedOrderNo',
      width: 150,
      render: (text: string) => text || '-',
    },
    {
      title: t('notification.record.table.sentTime'),
      dataIndex: 'sentTime',
      key: 'sentTime',
      width: 180,
      sorter: true,
      render: (time: number) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: t('notification.record.table.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      sorter: true,
      render: (time: number) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: t('notification.record.table.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            {t('notification.record.list.actions.detail')}
          </Button>
          {record.status === NotificationStatus.FAILED && (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleResend(record)}
            >
              {t('notification.record.list.actions.resend')}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    getCheckboxProps: (record: NotificationRecordDTO) => ({
      disabled: record.status !== NotificationStatus.FAILED,
    }),
  };

  // 是否有失败记录可选中批量重发
  const hasFailedRecord = dataSource.some(r => r.status === NotificationStatus.FAILED);

  return (
    <div className="notification-record-list" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 */}
      <div className="search-area">
        <Row gutter={[16, 16]}>
          <Col span={4}>
            <Input
              placeholder={t('notification.record.list.search.placeholder.notificationId')}
              value={searchParams.notificationId}
              onChange={(e) => handleSearchChange('notificationId', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.record.list.search.placeholder.notificationType')}
              value={searchParams.notificationType}
              onChange={(value) => handleSearchChange('notificationType', value)}
              allowClear
              style={{ width: '100%' }}
              options={NOTIFICATION_TYPE_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.label),
              }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.record.list.search.placeholder.channel')}
              value={searchParams.channel}
              onChange={(value) => handleSearchChange('channel', value)}
              allowClear
              style={{ width: '100%' }}
              options={NOTIFICATION_CHANNEL_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.label),
              }))}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder={t('notification.record.list.search.placeholder.status')}
              value={searchParams.status}
              onChange={(value) => handleSearchChange('status', value)}
              allowClear
              style={{ width: '100%' }}
              options={NOTIFICATION_STATUS_OPTIONS.map(opt => ({
                value: opt.value,
                label: t(opt.label),
              }))}
            />
          </Col>
          <Col span={4}>
            <Input
              placeholder={t('notification.record.list.search.placeholder.targetId')}
              value={searchParams.targetId}
              onChange={(e) => handleSearchChange('targetId', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Input
              placeholder={t('notification.record.list.search.placeholder.relatedOrderNo')}
              value={searchParams.relatedOrderNo}
              onChange={(e) => handleSearchChange('relatedOrderNo', e.target.value)}
              allowClear
            />
          </Col>
        </Row>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={8}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleTimeRangeChange}
              placeholder={[
                t('notification.record.list.search.placeholder.createTime'),
                t('notification.record.list.search.placeholder.createTime'),
              ]}
            />
          </Col>
          <Col span={16}>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                {t('common.search')}
              </Button>
              <Button onClick={handleReset}>
                {t('common.reset')}
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* 操作按钮区域 */}
      <div className="action-area" style={{ marginBottom: 16, flexShrink: 0 }}>
        <Space>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setSendFormVisible(true)}
          >
            {t('notification.record.list.actions.send')}
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleBatchResend}
            disabled={selectedRowKeys.length === 0 || !hasFailedRecord}
          >
            {t('notification.record.list.actions.batchResend')}
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        scroll={{ x: 1500, y: 'calc(100vh - 420px)' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => t('common.pagination.total', { total }),
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
        }}
      />

      {/* 详情 Drawer */}
      <NotificationRecordDetail
        visible={detailVisible}
        record={detailRecord}
        onClose={() => {
          setDetailVisible(false);
          setDetailRecord(null);
        }}
        onRefresh={fetchData}
      />

      {/* 发送通知 Drawer */}
      <SendNotificationForm
        visible={sendFormVisible}
        onClose={() => setSendFormVisible(false)}
        onSuccess={() => {
          setSendFormVisible(false);
          fetchData();
        }}
      />
    </div>
  );
};

export default NotificationRecordList;
